import type { PaginatedItems } from '@api/hooks/common.ts';
import { useBankProfile } from '@contexts/BankProfileContext.tsx';
import { useAuth } from '@hooks/useAuth.ts';
import apiClient from '@services/apiClient.ts';
import { useQuery } from '@tanstack/react-query';
import { AccountStatus } from 'types/operations.ts';

interface BalanceParams {
	offset?: number;
	limit?: number;
}

interface TotalBalance {
	available: number;
	balance: number;
	currency: string;
	reserved: number;
}

export interface AccountBalance {
	available: number;
	currency: string;
	reserved: number;
	balance: number;
	iban: string;
	status: AccountStatus;
	uuid: string;
	ownerName: string;
}

export const useBalance = ({ offset = 0, limit = 3 }: BalanceParams) => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	return useQuery({
		queryKey: ['balances', selectedProfile?.id, offset, limit],
		queryFn: async (): Promise<PaginatedItems<AccountBalance[]>> => {
			const response = await apiClient.get<PaginatedItems<AccountBalance[]>>(
				`/profiles/${selectedProfile?.id}/balances`,
				{
					params: { offset, limit },
				},
			);
			return response.data;
		},
		enabled: !!session?.tokenized && !!selectedProfile?.id,
		refetchOnWindowFocus: false,
		select: (data) => {
			return {
				...data,
				items: data.items.map((item) => ({
					...item,
					available: (item.available / 100).toFixed(2),
					balance: (item.balance / 100).toFixed(2),
					reserved: (item.reserved / 100).toFixed(2),
				})),
			};
		},
	});
};

export const useTotalBalance = () => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();

	return useQuery({
		queryKey: ['totalBalances', selectedProfile?.id],
		queryFn: async (): Promise<TotalBalance> => {
			const response = await apiClient.get<TotalBalance[]>(
				`/profiles/${selectedProfile?.id}/balances/totals`,
			);
			return (
				response.data.find((balance) => balance.currency === 'EUR') || {
					available: 0,
					balance: 0,
					currency: 'EUR',
					reserved: 0,
				}
			);
		},
		enabled: !!session?.tokenized && !!selectedProfile?.id,
		refetchOnWindowFocus: false,
		select: (data) => ({
			...data,
			available: (data.available / 100).toFixed(2),
			balance: (data.balance / 100).toFixed(2),
			reserved: (data.reserved / 100).toFixed(2),
		}),
	});
};
